/**
 * Admin PEAK Export Dashboard
 * Manage PEAK accounting exports with scheduling, history, and manual triggers
 */

import { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, CheckCircle2, Clock, Download, RefreshCw, Settings, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminPEAKExport() {
  const [activeTab, setActiveTab] = useState('overview');
  const [syncTime, setSyncTime] = useState('02:00');
  const [syncFrequency, setSyncFrequency] = useState('daily');
  const [maxRetries, setMaxRetries] = useState(3);
  const [retryDelayMinutes, setRetryDelayMinutes] = useState(60);
  const [dateFrom, setDateFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  // Queries
  const { data: syncStatus, isLoading: syncStatusLoading } = trpc.admin.peakDashboard.getSyncStatus.useQuery();
  const { data: exportHistory, isLoading: historyLoading } = trpc.admin.peakDashboard.getExportHistory.useQuery({
    limit: 20,
  });
  const { data: exportStats } = trpc.admin.peakDashboard.getExportStats.useQuery({
    dateFrom: new Date(dateFrom),
    dateTo: new Date(dateTo),
  });
  const { data: dataMapping } = trpc.admin.peakDashboard.getDataMappings.useQuery();
  const { data: categories } = trpc.admin.peakDashboard.getTransactionCategories.useQuery();

  // Mutations
  const updateConfigMutation = trpc.admin.peakDashboard.updateSyncConfig.useMutation({
    onSuccess: () => {
      alert('Sync configuration updated successfully');
    },
    onError: (error) => {
      alert(`Failed to update configuration: ${error.message}`);
    },
  });

  const triggerExportMutation = trpc.admin.peakDashboard.triggerExport.useMutation({
    onSuccess: (result: any) => {
      alert(`Export triggered successfully. Batch ID: ${result.batchId}`);
    },
    onError: (error) => {
      alert(`Failed to trigger export: ${error.message}`);
    },
  });

  const retryExportsMutation = trpc.admin.peakDashboard.retryFailedExports.useMutation({
    onSuccess: () => {
      alert('Retry process started');
    },
    onError: (error) => {
      alert(`Failed to start retry: ${error.message}`);
    },
  });

  const testConnectionMutation = trpc.admin.peakDashboard.testConnection.useMutation({
    onSuccess: () => {
      alert('PEAK API connection successful');
    },
    onError: (error) => {
      alert(`Connection test failed: ${error.message}`);
    },
  });

  // Handlers
  const handleUpdateConfig = async () => {
    await updateConfigMutation.mutateAsync({
      syncFrequency: syncFrequency as any,
      syncTime,
      maxRetries,
      retryDelayMinutes,
    });
  };

  const handleTriggerExport = async () => {
    await triggerExportMutation.mutateAsync({
      dateFrom: new Date(dateFrom),
      dateTo: new Date(dateTo),
      exportType: 'manual',
    });
  };

  const handleRetryExports = async () => {
    await retryExportsMutation.mutateAsync();
  };

  const handleTestConnection = async () => {
    await testConnectionMutation.mutateAsync();
  };

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      default:
        return <TrendingUp className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">PEAK Accounting Export</h1>
          <p className="text-gray-600 mt-1">Manage automated exports to PEAK accounting software</p>
        </div>
        <Button onClick={handleTestConnection} variant="outline" disabled={testConnectionMutation.isPending}>
          {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="mappings">Mappings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Sync Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {syncStatus?.data?.isEnabled ? 'Enabled' : 'Disabled'}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {syncStatus?.data?.syncFrequency || 'N/A'} exports
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Exports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{exportStats?.data?.totalExports || 0}</div>
                <p className="text-xs text-gray-500 mt-1">
                  {exportStats?.data?.successfulExports || 0} successful
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Records Exported</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{exportStats?.data?.totalRecords || 0}</div>
                <p className="text-xs text-gray-500 mt-1">Total records sent</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Failed Exports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{exportStats?.data?.failedExports || 0}</div>
                <p className="text-xs text-gray-500 mt-1">Pending retry</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Manual Export</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleTriggerExport}
                    disabled={triggerExportMutation.isPending}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {triggerExportMutation.isPending ? 'Exporting...' : 'Export'}
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRetryExports}
                  variant="outline"
                  disabled={retryExportsMutation.isPending}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {retryExportsMutation.isPending ? 'Retrying...' : 'Retry Failed Exports'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Exports */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Exports</CardTitle>
              <CardDescription>Last 5 export attempts</CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (exportHistory?.data?.length || 0) > 0 ? (
                <div className="space-y-3">
                  {exportHistory?.data?.slice(0, 5).map((exp: any) => (
                    <div key={exp.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(exp.status)}
                        <div>
                          <p className="font-medium">{exp.exportType} Export</p>
                          <p className="text-sm text-gray-500">
                            {format(new Date(exp.createdAt), 'MMM dd, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-medium">{exp.recordCount} records</p>
                          <p className="text-sm text-gray-500">Batch: {exp.batchId}</p>
                        </div>
                        <Badge className={getStatusColor(exp.status)}>{exp.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No exports yet</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Export History</CardTitle>
              <CardDescription>Complete list of all export attempts</CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : (exportHistory?.data?.length || 0) > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Records</th>
                        <th className="text-left py-2 px-2">Batch ID</th>
                        <th className="text-left py-2 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exportHistory?.data?.map((exp: any) => (
                        <tr key={exp.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2">{format(new Date(exp.createdAt), 'MMM dd, yyyy HH:mm')}</td>
                          <td className="py-2 px-2 capitalize">{exp.exportType}</td>
                          <td className="py-2 px-2">{exp.recordCount}</td>
                          <td className="py-2 px-2 font-mono text-xs">{exp.batchId}</td>
                          <td className="py-2 px-2">
                            <Badge className={getStatusColor(exp.status)}>{exp.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No export history</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Export Schedule Configuration</CardTitle>
              <CardDescription>Configure automatic export scheduling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sync-frequency">Sync Frequency</Label>
                  <Select value={syncFrequency} onValueChange={setSyncFrequency}>
                    <SelectTrigger id="sync-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sync-time">Sync Time (UTC)</Label>
                  <Input
                    id="sync-time"
                    type="time"
                    value={syncTime}
                    onChange={(e) => setSyncTime(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max-retries">Max Retries</Label>
                  <Input
                    id="max-retries"
                    type="number"
                    min="1"
                    max="10"
                    value={maxRetries}
                    onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="retry-delay">Retry Delay (minutes)</Label>
                  <Input
                    id="retry-delay"
                    type="number"
                    min="5"
                    max="1440"
                    value={retryDelayMinutes}
                    onChange={(e) => setRetryDelayMinutes(parseInt(e.target.value))}
                  />
                </div>
              </div>

              <Button onClick={handleUpdateConfig} disabled={updateConfigMutation.isPending} className="gap-2">
                <Settings className="w-4 h-4" />
                {updateConfigMutation.isPending ? 'Updating...' : 'Update Configuration'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mappings Tab */}
        <TabsContent value="mappings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Code Mappings</CardTitle>
              <CardDescription>Data field mappings to PEAK account codes</CardDescription>
            </CardHeader>
            <CardContent>
              {dataMapping?.data?.length || 0 > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left py-2 px-2">Field</th>
                        <th className="text-left py-2 px-2">PlayXWorld Value</th>
                        <th className="text-left py-2 px-2">PEAK Account Code</th>
                        <th className="text-left py-2 px-2">Description</th>
                        <th className="text-left py-2 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataMapping?.data?.map((map: any) => (
                        <tr key={map.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-2 font-mono text-xs">{map.mappingKey}</td>
                          <td className="py-2 px-2">{map.playxworldValue}</td>
                          <td className="py-2 px-2 font-mono">{map.peakValue}</td>
                          <td className="py-2 px-2">{map.description}</td>
                          <td className="py-2 px-2">
                            <Badge variant={map.isActive ? 'default' : 'secondary'}>
                              {map.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No mappings configured</div>
              )}
            </CardContent>
          </Card>

          {/* Transaction Categories */}
          <Card>
            <CardHeader>
              <CardTitle>Transaction Categories</CardTitle>
              <CardDescription>Supported transaction types for export</CardDescription>
            </CardHeader>
            <CardContent>
              {categories?.data?.length || 0 > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories?.data?.map((cat: any) => (
                    <div key={cat.id} className="p-3 border rounded-lg">
                      <p className="font-medium">{cat.name}</p>
                      <p className="text-sm text-gray-500">{cat.type}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No categories available</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
